import type { ReactNode } from 'react'
import { Spinner } from './Spinner'

interface ButtonProps {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
  variant?: 'primary' | 'secondary' | 'ghost'
  /** `lg` is the page's one main action (the Executive report's "See the fix plan", DEC-190). */
  size?: 'sm' | 'md' | 'lg'
  type?: 'button' | 'submit'
  className?: string
}

/**
 * Actions are pills (UI_SDD §R.1, DEC-127.5).
 *
 * `primary` hover darkens one ramp step, #805EC5 → #6A4AAB, which is the brand's
 * own rule; the pre-DEC-127 `hover:bg-indigo-600` left the purple family
 * entirely. `secondary` is an outline rather than a white fill: the design
 * system's `inverse` variant (white ground, purple label) is a marketing
 * treatment and reads as a primary action on a near-black product surface.
 *
 * Padding stays at the app's density rather than the design system's 12px/26px
 * marketing scale. §R.1 fixes the radius, not the padding.
 */
const VARIANT_CLASSES = {
  primary: 'bg-accent text-white hover:bg-[#6A4AAB] active:bg-[#553A8B] disabled:opacity-50',
  secondary:
    'bg-transparent border border-hairline-strong text-ink hover:bg-hover-hue disabled:opacity-50',
  ghost: 'text-body hover:text-ink hover:bg-hover-hue disabled:opacity-50',
}

const SIZE_CLASSES = {
  sm: 'px-3.5 py-1.5 text-sm',
  md: 'px-[18px] py-2 text-sm',
  lg: 'px-7 py-[14px] text-[16px]',
}

export function Button({
  children,
  onClick,
  disabled,
  loading,
  variant = 'primary',
  size = 'md',
  type = 'button',
  className = '',
}: ButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-md font-semibold whitespace-nowrap transition-colors focus:outline-none focus-visible:shadow-focus active:scale-[.98] disabled:active:scale-100 disabled:cursor-not-allowed ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${className}`}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  )
}
